/**
 * ニュースレターモーダル用 JavaScript (AJAX対応)
 */

class NewsletterModal extends HTMLElement {
  constructor() {
    super();
    this.modal = document.querySelector('#NewsletterModal');
    this.form = this.modal?.querySelector('#newsletter-modal-form');
    this.submitButton = this.form?.querySelector('.newsletter-form__submit-button');
    this.emailInput = this.form?.querySelector('input[type="email"]');
    this.messageContainer = this.form?.querySelector('.newsletter-form__message-container');
    this.successContainer = this.form?.querySelector('.newsletter-form__success-container');

    this.init();
  }

  init() {
    if (!this.modal || !this.form) return;

    // フォーム送信時の処理 (AJAX)
    this.form.addEventListener('submit', this.handleSubmit.bind(this));

    // モーダルが開かれた時の処理
    this.modal.addEventListener('modalOpened', this.handleModalOpened.bind(this));

    // モーダルが閉じられた時の処理
    this.modal.addEventListener('modalClosed', this.handleModalClosed.bind(this));

    // ESCキーでモーダルを閉じる
    document.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  async handleSubmit(event) {
    event.preventDefault(); // デフォルトの送信を防ぐ

    if (!this.emailInput.value.trim()) {
      this.showError('メールアドレスを入力してください。');
      return;
    }

    if (!this.isValidEmail(this.emailInput.value)) {
      this.showError('有効なメールアドレスを入力してください。');
      return;
    }

    // 送信中の状態を表示
    this.setLoadingState(true);
    this.clearMessages();

    try {
      // FormDataを作成
      const formData = new FormData(this.form);

      // AJAX送信
      const response = await fetch(this.form.action || window.location.pathname, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      const responseText = await response.text();

      // レスポンスをパース（HTMLの場合）
      const parser = new DOMParser();
      const doc = parser.parseFromString(responseText, 'text/html');

      // 成功したかどうかをチェック
      const successIndicator =
        doc.querySelector('.newsletter-form__message--success') ||
        responseText.includes('newsletter-success') ||
        (response.ok && !doc.querySelector('.form__message'));

      if (successIndicator) {
        this.showSuccess();
        // アナリティクス追跡
        this.trackEvent('newsletter_signup', 'success');
      } else {
        // エラーメッセージを抽出
        const errorElement = doc.querySelector('.form__message');
        const errorMessage = errorElement
          ? errorElement.textContent.trim()
          : 'エラーが発生しました。もう一度お試しください。';
        this.showError(errorMessage);
      }
    } catch (error) {
      console.error('Newsletter submission error:', error);
      this.showError('ネットワークエラーが発生しました。もう一度お試しください。');
    } finally {
      this.setLoadingState(false);
    }
  }

  handleModalOpened() {
    // モーダルが開かれた時にメールフィールドにフォーカス
    setTimeout(() => {
      if (this.emailInput) {
        this.emailInput.focus();
      }
    }, 100);

    // 前回のメッセージをクリア
    this.clearMessages();

    // アナリティクス追跡
    this.trackEvent('newsletter_modal', 'opened');
  }

  handleModalClosed() {
    // フォームをリセット
    this.clearMessages();
    this.setLoadingState(false);

    // 成功状態の場合はフォームもリセット
    if (this.successContainer.style.display !== 'none') {
      this.form.reset();
    }

    // アナリティクス追跡
    this.trackEvent('newsletter_modal', 'closed');
  }

  handleKeydown(event) {
    // ESCキーでモーダルを閉じる
    if (event.key === 'Escape' && this.modal.hasAttribute('open')) {
      this.modal.hide();
    }
  }

  setLoadingState(isLoading) {
    if (!this.submitButton) return;

    if (isLoading) {
      this.submitButton.disabled = true;
      this.submitButton.classList.add('loading');
      this.submitButton.setAttribute('aria-label', '送信中...');
    } else {
      this.submitButton.disabled = false;
      this.submitButton.classList.remove('loading');
      this.submitButton.setAttribute('aria-label', 'ニュースレターに登録');
    }
  }

  showSuccess() {
    this.clearMessages();

    const successMessage =
      document.querySelector('[data-newsletter-success]')?.textContent || 'ご登録ありがとうございます。';

    const successHtml = `
      <div class="newsletter-form__message newsletter-form__message--success">
        <svg class="icon icon-success" viewBox="0 0 24 24" role="img" aria-hidden="true">
          <path fill="currentColor" d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"/>
        </svg>
        ${successMessage}
      </div>
    `;

    this.successContainer.innerHTML = successHtml;
    this.successContainer.style.display = 'block';

    // フォームを隠す
    this.form.querySelector('.newsletter-form__field-wrapper').style.display = 'none';
    this.form.querySelector('.newsletter-form__button-wrapper').style.display = 'none';

    // 3秒後に自動でモーダルを閉じる
    setTimeout(() => {
      if (this.modal.hasAttribute('open')) {
        this.modal.hide();
        // リセット処理
        setTimeout(() => this.resetForm(), 300);
      }
    }, 3000);
  }

  showError(message) {
    this.clearMessages();

    const errorHtml = `
      <div class="newsletter-form__message newsletter-form__message--error">
        <svg class="icon icon-error" viewBox="0 0 24 24" role="img" aria-hidden="true">
          <path fill="currentColor" d="M12,2L13.09,8.26L22,9L13.09,9.74L12,16L10.91,9.74L2,9L10.91,8.26L12,2Z"/>
        </svg>
        ${message}
      </div>
    `;

    this.messageContainer.innerHTML = errorHtml;

    // エラーメッセージにフォーカス（アクセシビリティ）
    const errorElement = this.messageContainer.querySelector('.newsletter-form__message--error');
    if (errorElement) {
      errorElement.tabIndex = -1;
      errorElement.focus();
    }
  }

  clearMessages() {
    if (this.messageContainer) {
      this.messageContainer.innerHTML = '';
    }
    if (this.successContainer) {
      this.successContainer.innerHTML = '';
      this.successContainer.style.display = 'none';
    }
  }

  resetForm() {
    // フォーム表示を元に戻す
    this.form.querySelector('.newsletter-form__field-wrapper').style.display = 'block';
    this.form.querySelector('.newsletter-form__button-wrapper').style.display = 'block';
    this.form.reset();
    this.clearMessages();
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  trackEvent(action, label) {
    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', action, {
        event_category: 'newsletter',
        event_label: label,
        custom_parameter: 'modal',
      });
    }

    // Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('track', 'Lead');
    }
  }
}

// DOMが読み込まれた後に初期化
document.addEventListener('DOMContentLoaded', () => {
  // カスタムエレメントとして登録
  if (!customElements.get('newsletter-modal-enhanced')) {
    customElements.define('newsletter-modal-enhanced', NewsletterModal);
  }

  // インスタンスを作成
  new NewsletterModal();

  // 既存のモーダルシステムを拡張
  const newsletterModal = document.querySelector('#NewsletterModal');
  if (newsletterModal) {
    // モーダル開閉イベントの追加
    const originalShow = newsletterModal.show;
    const originalHide = newsletterModal.hide;

    if (originalShow) {
      newsletterModal.show = function (opener) {
        originalShow.call(this, opener);
        this.dispatchEvent(new CustomEvent('modalOpened'));
      };
    }

    if (originalHide) {
      newsletterModal.hide = function () {
        originalHide.call(this);
        this.dispatchEvent(new CustomEvent('modalClosed'));
      };
    }
  }
});

// エクスポート（モジュール使用時）
if (typeof module !== 'undefined' && module.exports) {
  module.exports = NewsletterModal;
}
